import { computed, inject, Injectable, signal } from '@angular/core';
import { AuthService } from '@brightside/core/auth';
import {
  ConversationalBtmActiveRun,
  ConversationalBtmApiError,
  ConversationalBtmApiService,
  ConversationalBtmConversationDetail,
  ConversationalBtmConversationMetadata,
  ConversationalBtmExecutionStatus,
  ConversationalBtmRunState,
  mapConversationalBtmDetail,
  mapConversationalBtmRunState,
} from '@brighthub/copilot';
import {
  ASK_NAVI_AGENT_CONFIG,
  ASK_NAVI_CONVERSATION_METADATA,
} from '../config/ask-navi-agent.config';
import {
  ConversationListItem,
  Message,
  MessageRole,
} from '../models/ask-navi-chat.models';
import { deriveConversationTitle } from '../utils/derive-conversation-title';
import { isAskNaviConversation } from '../utils/is-ask-navi-conversation';

const CONVERSATION_PAGE_SIZE = 100;
const CONVERSATION_LIST_LIMIT = 20;

@Injectable({ providedIn: 'root' })
export class AskNaviChatStateService {
  private readonly btm = inject(ConversationalBtmApiService);
  private readonly auth = inject(AuthService);
  private runController?: AbortController;
  private activeRunStartedAt = '';
  private selectionGeneration = 0;

  readonly conversations = signal<ConversationListItem[]>([]);
  readonly activeConversationId = signal<string | null>(null);
  readonly messages = signal<Message[]>([]);
  readonly activeRun = signal<ConversationalBtmActiveRun | null>(null);
  readonly executionStatus = signal<ConversationalBtmExecutionStatus>('idle');
  readonly isLoadingConversations = signal(false);
  readonly isLoadingMessages = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly sidebarCollapsed = signal(false);
  readonly draftInput = signal('');

  readonly activeConversation = computed(
    () =>
      this.conversations().find(
        (conversation) => conversation.id === this.activeConversationId()
      ) ?? null
  );

  readonly activeAgentIdentifier = computed(
    () =>
      agentIdentifierFromMetadata(this.activeConversation()?.metadata) ??
      ASK_NAVI_AGENT_CONFIG.agentIdentifier
  );

  readonly isInputDisabled = computed(() => {
    const execution = this.executionStatus();
    return (
      this.isLoadingMessages() ||
      this.activeRun() !== null ||
      execution === 'queued' ||
      execution === 'running' ||
      execution === 'waiting_for_input'
    );
  });

  readonly isStreaming = computed(() => {
    const status = this.activeRun()?.status;
    return Boolean(status && status !== 'idle' && status !== 'disconnected');
  });

  readonly canContinueReply = computed(
    () => this.activeRun()?.status === 'disconnected'
  );

  readonly isSubmitting = computed(
    () => this.activeRun()?.status === 'submitting'
  );

  readonly streamingMessageIds = computed(() =>
    this.messages()
      .filter((message) => message.metadata?.['streaming'] === true)
      .map((message) => message.id)
  );

  readonly showWelcome = computed(
    () =>
      this.activeConversationId() === null ||
      (this.messages().length === 0 && this.activeRun() === null)
  );

  readonly showThinking = computed(() => {
    if (!this.isStreaming()) return false;
    const streamingMessages = this.messages().filter(
      (message) => message.metadata?.['streaming'] === true
    );
    return (
      streamingMessages.length === 0 ||
      streamingMessages.every((message) => !message.content.trim())
    );
  });

  readonly userDisplayName = computed(() => {
    const name = this.auth.userDisplayName();
    return name.trim() || 'You';
  });

  readonly userInitials = computed(() => {
    const name = this.userDisplayName();
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase() || 'U';
  });

  async loadConversations(): Promise<void> {
    this.isLoadingConversations.set(true);
    this.errorMessage.set(null);
    try {
      const conversations = new Map<string, ConversationListItem>();
      const cursors = new Set<string>();
      let after: string | undefined;
      for (;;) {
        const page = await this.btm.listConversations({
          limit: CONVERSATION_PAGE_SIZE,
          after,
        });
        for (const item of page.items.filter(isAskNaviConversation)) {
          const conversation = toConversationListItem(item);
          if (!conversations.has(conversation.id)) {
            conversations.set(conversation.id, conversation);
          }
        }
        if (
          conversations.size >= CONVERSATION_LIST_LIMIT ||
          !page.has_more ||
          !page.next_cursor ||
          cursors.has(page.next_cursor)
        ) {
          break;
        }
        cursors.add(page.next_cursor);
        after = page.next_cursor;
      }
      this.conversations.set(
        [...conversations.values()].slice(0, CONVERSATION_LIST_LIMIT)
      );
    } catch {
      this.errorMessage.set('Failed to load conversations.');
    } finally {
      this.isLoadingConversations.set(false);
    }
  }

  startNewChat(): void {
    if (this.isSubmitting()) return;
    this.selectionGeneration += 1;
    this.abortActiveRun();
    this.activeConversationId.set(null);
    this.messages.set([]);
    this.executionStatus.set('idle');
    this.isLoadingMessages.set(false);
    this.draftInput.set('');
    this.errorMessage.set(null);
  }

  async selectConversation(conversationId: string): Promise<void> {
    if (this.isSubmitting()) return;
    const generation = ++this.selectionGeneration;
    this.abortActiveRun();
    this.activeConversationId.set(conversationId);
    this.isLoadingMessages.set(true);
    this.errorMessage.set(null);
    try {
      const detail = await this.btm.getCompleteConversation(conversationId);
      if (!this.isCurrentSelection(conversationId, generation)) return;
      this.hydrateDetail(detail);
      const execution = detail.conversation.execution;
      if (
        (execution.status === 'queued' || execution.status === 'running') &&
        execution.run_id &&
        execution.stream_url
      ) {
        void this.followRun({
          conversationId,
          runId: execution.run_id,
          streamUrl: execution.stream_url,
          initialDetail: detail,
        });
      }
    } catch {
      if (!this.isCurrentSelection(conversationId, generation)) return;
      this.errorMessage.set('Failed to load messages.');
      this.messages.set([]);
      this.executionStatus.set('idle');
    } finally {
      if (this.isCurrentSelection(conversationId, generation)) {
        this.isLoadingMessages.set(false);
      }
    }
  }

  async deleteConversation(conversationId: string): Promise<void> {
    if (!conversationId || this.isInputDisabled()) return;
    this.errorMessage.set(null);
    try {
      await this.btm.deleteConversation(conversationId);
      this.conversations.update((conversations) =>
        conversations.filter(({ id }) => id !== conversationId)
      );
      if (this.activeConversationId() === conversationId) {
        this.startNewChat();
      }
    } catch {
      this.errorMessage.set(
        'Failed to delete the conversation. Please try again.'
      );
    }
  }

  async sendMessage(content: string): Promise<void> {
    const trimmed = content.trim();
    if (!trimmed || this.isInputDisabled()) return;

    this.errorMessage.set(null);
    this.draftInput.set('');
    const existingConversationId = this.activeConversationId();
    const title = existingConversationId
      ? undefined
      : deriveConversationTitle(trimmed);
    const optimisticId = `user-${uuid()}`;
    const optimisticMessage: Message = {
      id: optimisticId,
      conversation_id: existingConversationId ?? 'pending',
      role: MessageRole.USER,
      content: trimmed,
      created_at: new Date().toISOString(),
      metadata: { temporary: true, finalized: false },
    };
    this.messages.update((messages) => [...messages, optimisticMessage]);
    this.activeRun.set({
      runId: 'pending',
      streamUrl: '',
      attempt: 0,
      status: 'submitting',
    });

    let acceptedConversationId: string | undefined;
    try {
      const accepted = await this.btm.submit(
        {
          ...(existingConversationId
            ? { conversation_id: existingConversationId }
            : { title }),
          agent_identifier: ASK_NAVI_AGENT_CONFIG.agentIdentifier,
          input: { type: 'message', content: trimmed },
        },
        uuid()
      );
      if (
        existingConversationId &&
        accepted.conversation_id !== existingConversationId
      ) {
        throw new ConversationalBtmApiError(
          'The conversational API returned a different conversation id.',
          { status: 502, code: 'CONVERSATION_ID_MISMATCH' }
        );
      }

      acceptedConversationId = accepted.conversation_id;
      this.activeConversationId.set(accepted.conversation_id);
      this.messages.update((messages) =>
        messages.map((message) =>
          message.id === optimisticId
            ? { ...message, conversation_id: accepted.conversation_id }
            : message
        )
      );
      if (!existingConversationId) {
        const now = new Date().toISOString();
        this.conversations.update((conversations) => [
          {
            id: accepted.conversation_id,
            title: title ?? 'New conversation',
            created_at: now,
            last_activity: now,
            metadata: ASK_NAVI_CONVERSATION_METADATA,
          },
          ...conversations,
        ]);
      }
      this.activeRunStartedAt = new Date().toISOString();
      await this.followRun({
        conversationId: accepted.conversation_id,
        runId: accepted.run_id,
        streamUrl: accepted.stream_url,
      });
    } catch (error) {
      if (isAbort(error)) return;
      if (
        error instanceof ConversationalBtmApiError &&
        error.status === 409 &&
        existingConversationId
      ) {
        this.messages.update((messages) =>
          messages.filter((message) => message.id !== optimisticId)
        );
        this.activeRun.set(null);
        await this.recoverConversation(existingConversationId);
        return;
      }
      if (!acceptedConversationId) {
        this.messages.update((messages) =>
          messages.filter((message) => message.id !== optimisticId)
        );
        this.activeRun.set(null);
        this.executionStatus.set('idle');
        this.errorMessage.set('Failed to send your message. Please try again.');
      }
    }
  }

  async continueReply(): Promise<void> {
    const conversationId = this.activeConversationId();
    if (!conversationId || !this.canContinueReply()) return;
    this.errorMessage.set(null);
    await this.recoverConversation(conversationId);
  }

  toggleSidebar(): void {
    this.sidebarCollapsed.update((collapsed) => !collapsed);
  }

  private async recoverConversation(conversationId: string): Promise<void> {
    try {
      const detail = await this.btm.getCompleteConversation(conversationId);
      if (this.activeConversationId() !== conversationId) return;
      this.hydrateDetail(detail);
      const execution = detail.conversation.execution;
      if (
        (execution.status === 'queued' || execution.status === 'running') &&
        execution.run_id &&
        execution.stream_url
      ) {
        await this.followRun({
          conversationId,
          runId: execution.run_id,
          streamUrl: execution.stream_url,
          initialDetail: detail,
        });
      } else {
        this.abortActiveRun();
      }
    } catch (error) {
      if (!isAbort(error) && this.activeConversationId() === conversationId) {
        this.errorMessage.set('Failed to continue loading the reply.');
      }
    }
  }

  private async followRun(options: {
    conversationId: string;
    runId: string;
    streamUrl: string;
    initialDetail?: ConversationalBtmConversationDetail;
  }): Promise<void> {
    this.abortActiveRun();
    const controller = new AbortController();
    this.runController = controller;
    if (!this.activeRunStartedAt) this.activeRunStartedAt = new Date().toISOString();
    this.activeRun.set({
      runId: options.runId,
      streamUrl: options.streamUrl,
      attempt: 0,
      status: 'connecting',
    });
    this.executionStatus.set('running');

    try {
      const result = await this.btm.followRun({
        conversationId: options.conversationId,
        runId: options.runId,
        streamUrl: options.streamUrl,
        signal: controller.signal,
        initialMessages: options.initialDetail
          ? initialRunMessages(options.initialDetail, options.runId)
          : undefined,
        onConnectionChange: (status, attempt) => {
          if (this.runController !== controller) return;
          this.activeRun.update((run) =>
            run ? { ...run, status, attempt } : run
          );
        },
        onSnapshot: (snapshot) => {
          if (this.runController !== controller) return;
          this.replaceRunSnapshot(options.conversationId, snapshot);
          this.activeRun.update((run) =>
            run
              ? { ...run, lastEventId: snapshot.lastEventId, error: snapshot.error }
              : run
          );
        },
      });
      if (this.runController !== controller) return;
      this.hydrateDetail(result.detail);
      this.runController = undefined;
      this.activeRun.set(null);
      if (result.state.terminal === 'error') {
        this.errorMessage.set(result.state.error ?? 'The agent run failed.');
      }
      void this.loadConversations();
    } catch (error) {
      if (this.runController !== controller || isAbort(error)) return;
      if (
        error instanceof ConversationalBtmApiError &&
        error.code === 'RUN_DISCONNECTED'
      ) {
        this.activeRun.update((run) =>
          run
            ? { ...run, status: 'disconnected', error: error.message }
            : run
        );
        this.errorMessage.set(
          'The reply is still running, but the live connection was lost.'
        );
      } else {
        this.runController = undefined;
        this.activeRun.set(null);
        this.executionStatus.set('idle');
        this.errorMessage.set('Failed to load the assistant reply.');
      }
    }
  }

  private replaceRunSnapshot(
    conversationId: string,
    state: ConversationalBtmRunState
  ): void {
    const mapped = mapConversationalBtmRunState(
      state,
      conversationId,
      this.activeRunStartedAt
    );
    const streamed: Message[] = mapped
      ? [{ ...mapped, role: MessageRole.ASSISTANT } as Message]
      : [];
    this.messages.update((messages) => [
      ...messages.filter(
        (message) =>
          !(
            message.role === MessageRole.ASSISTANT &&
            message.metadata?.['run_id'] === state.runId
          )
      ),
      ...streamed,
    ]);
  }

  private hydrateDetail(detail: ConversationalBtmConversationDetail): void {
    const mapped = mapConversationalBtmDetail(detail);
    this.messages.set(
      mapped.messages.map((message) => ({
        ...message,
        role: toLocalRole(message.role),
      }))
    );
    this.executionStatus.set(detail.conversation.execution.status);
  }

  private isCurrentSelection(
    conversationId: string,
    generation: number
  ): boolean {
    return (
      generation === this.selectionGeneration &&
      this.activeConversationId() === conversationId
    );
  }

  private abortActiveRun(): void {
    this.runController?.abort();
    this.runController = undefined;
    this.activeRun.set(null);
    this.activeRunStartedAt = '';
  }
}

function toConversationListItem(
  conversation: ConversationalBtmConversationMetadata
): ConversationListItem {
  return {
    id: conversation.id,
    title: conversation.title,
    created_at: conversation.created_at,
    last_activity: conversation.last_activity,
    owner_id: conversation.owner_id,
    metadata: conversation.metadata,
  };
}

function initialRunMessages(
  detail: ConversationalBtmConversationDetail,
  runId: string
) {
  return detail.messages.flatMap((message) =>
    message.run_id === runId && message.role === 'assistant'
      ? [
          {
            id: message.id,
            content: message.content ?? '',
            parts: message.parts ?? [],
          },
        ]
      : []
  );
}

function toLocalRole(role: string): MessageRole {
  if (role === MessageRole.USER) return MessageRole.USER;
  if (role === MessageRole.ASSISTANT) return MessageRole.ASSISTANT;
  return MessageRole.SYSTEM;
}

function agentIdentifierFromMetadata(
  metadata: Record<string, unknown> | undefined
): string | undefined {
  const identifier = metadata?.['agent_identifier'];
  return typeof identifier === 'string' && identifier.trim()
    ? identifier.trim()
    : undefined;
}

function uuid(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
}

function isAbort(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { name?: unknown }).name === 'AbortError'
  );
}
